import { Volume } from "../Volume";
import { VolumeManager } from "../VolumeManager";
import { ILaya } from "../../../../../ILaya";
import { AmbientMode } from "../../../core/scene/AmbientMode";
import { Color } from "../../../../maths/Color";
import { Vector3 } from "../../../../maths/Vector3";
import { Vector4 } from "../../../../maths/Vector4";
import { Laya3DRender } from "../../../RenderObjs/Laya3DRender";
import { ShaderDataType } from "../../../../RenderDriver/DriverDesign/RenderDevice/ShaderData";
import { LayaGL } from "../../../../layagl/LayaGL";
import { Shader3D } from "../../../../RenderEngine/RenderShader/Shader3D";
export var ReflectionProbeMode;
(function (ReflectionProbeMode) {
    ReflectionProbeMode[ReflectionProbeMode["off"] = 0] = "off";
    ReflectionProbeMode[ReflectionProbeMode["simple"] = 1] = "simple";
})(ReflectionProbeMode || (ReflectionProbeMode = {}));
export class ReflectionProbe extends Volume {
    static getID() {
        return ReflectionProbe.reflectionCount++;
    }
    static init() {
        ReflectionProbe.SHADERDEFINE_GI_IBL = Shader3D.getDefineByName("GI_IBL");
        let unifomrMap = ReflectionProbe.CommandMap = LayaGL.renderDeviceFactory.createGlobalUniformMap(ReflectionProbe.BlockName);
        const addUniform = (name, type, arrayLength = 0) => {
            let unifomrIndex = Shader3D.propertyNameToID(name);
            if (arrayLength > 0) {
                unifomrMap.addShaderUniformArray(unifomrIndex, name, type, arrayLength);
            }
            else {
                unifomrMap.addShaderUniform(unifomrIndex, name, type);
            }
            return unifomrIndex;
        };
        ReflectionProbe.AMBIENTCOLOR = addUniform("u_AmbientColor", ShaderDataType.Vector4);
        ReflectionProbe.AMBIENTSH = addUniform("u_IblSH", ShaderDataType.Vector3, 9);
        ReflectionProbe.IBLTEX = addUniform("u_IBLTex", ShaderDataType.TextureCube);
        ReflectionProbe.IBLROUGHNESSLEVEL = addUniform("u_IBLRoughnessLevel", ShaderDataType.Float);
        ReflectionProbe.AMBIENTINTENSITY = addUniform("u_AmbientIntensity", ShaderDataType.Float);
        ReflectionProbe.REFLECTIONINTENSITY = addUniform("u_ReflectionIntensity", ShaderDataType.Float);
        ReflectionProbe.REFLECTIONCUBE_PROBEPOSITION = addUniform("u_SpecCubeProbePosition", ShaderDataType.Vector3);
        ReflectionProbe.REFLECTIONCUBE_PROBEBOXMAX = addUniform("u_SpecCubeBoxMax", ShaderDataType.Vector3);
        ReflectionProbe.REFLECTIONCUBE_PROBEBOXMIN = addUniform("u_SpecCubeBoxMin", ShaderDataType.Vector3);
    }
    constructor() {
        super();
        this._ambientColor = new Color();
        this._isScene = false;
        this._importance = 0;
        this._type = VolumeManager.ReflectionProbeVolumeType;
        this._dataModule = Laya3DRender.Render3DModuleDataFactory.createReflectionProbe();
        this._dataModule.bound = this._bounds;
        this.ambientIntensity = 1.0;
        this.reflectionIntensity = 1.0;
        this.boundsMax = new Vector3(5, 5, 5);
        this.boundsMin = new Vector3(-5, -5, -5);
        this._reflectionProbeID = ReflectionProbe.getID();
        this.ambientMode = AmbientMode.SolidColor;
        this._dataModule.updateMark = -1;
    }
    get shaderData() {
        return this._dataModule.shaderData;
    }
    get boxProjection() {
        return this._dataModule.boxProjection;
    }
    set boxProjection(value) {
        if (value != this._dataModule.boxProjection) {
            this._dataModule.updateMark = ILaya.Scene3D._updateMark;
        }
        this._dataModule.boxProjection = value;
    }
    get importance() {
        return this._importance;
    }
    set importance(value) {
        this._importance = value;
    }
    get ambientIntensity() {
        return this._dataModule.ambientIntensity;
    }
    set ambientIntensity(value) {
        if (value == this._dataModule.ambientIntensity)
            return;
        this._dataModule.ambientIntensity = value;
        this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    get reflectionIntensity() {
        return this._dataModule.reflectionIntensity;
    }
    set reflectionIntensity(value) {
        if (value == this._dataModule.reflectionIntensity)
            return;
        value = Math.max(value, 0.0);
        this._dataModule.reflectionIntensity = value;
        this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    _reCaculateBoundBox() {
        super._reCaculateBoundBox();
        this.owner && this._dataModule.setProbePosition(this.owner.transform.position);
        this.bounds.cloneTo(this._dataModule.bound);
    }
    get bounds() {
        return this._bounds;
    }
    get boundsMax() {
        return this._primitiveBounds.getMax();
    }
    set boundsMax(value) {
        super.boundsMax = value;
        if (this.boxProjection)
            this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    get boundsMin() {
        return this._primitiveBounds.getMin();
    }
    set boundsMin(value) {
        super.boundsMin = value;
        if (this.boxProjection)
            this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    get probePosition() {
        return this.owner.transform.position;
    }
    get ambientColor() {
        return this._ambientColor;
    }
    set ambientColor(value) {
        value.cloneTo(this._ambientColor);
        this._dataModule.setAmbientColor(this._ambientColor);
        if (this.ambientMode == AmbientMode.SolidColor)
            this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    get ambientSH() {
        return this._ambientSH;
    }
    set ambientSH(value) {
        if (this.ambientMode == AmbientMode.SphericalHarmonics)
            this._dataModule.updateMark = ILaya.Scene3D._updateMark;
        this._ambientSH = value;
        this._dataModule.setAmbientSH(this._ambientSH);
    }
    get ambientMode() {
        return this._dataModule.ambientMode;
    }
    set ambientMode(value) {
        if (value == this.ambientMode)
            return;
        this._dataModule.ambientMode = value;
        this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    get iblTex() {
        return this._iblTex;
    }
    set iblTex(value) {
        if (this._iblTex == value)
            return;
        if (this._iblTex)
            this._iblTex._removeReference();
        this._iblTex = value;
        this._dataModule.iblTex = null;
        if (value) {
            value._addReference();
            this._dataModule.iblTex = value._texture;
        }
        this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    get iblTexRGBD() {
        return this._dataModule.iblTexRGBD;
    }
    set iblTexRGBD(value) {
        if (value == this._dataModule.iblTexRGBD)
            return;
        this._dataModule.iblTexRGBD = value;
        this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    _onEnable() {
        super._onEnable();
        this._dataModule.updateMark = ILaya.Scene3D._updateMark;
    }
    _onDestroy() {
        this.iblTex = null;
        this._dataModule.destroy();
    }
}
ReflectionProbe.BlockName = "ReflectionProbe";
ReflectionProbe.reflectionCount = 0;
ReflectionProbe.defaultTextureHDRDecodeValues = new Vector4(1.0, 1.0, 0.0, 0.0);
